import _                            from 'underscore';
import React, { FunctionComponent } from 'react';
import ReactDOM                     from 'react-dom';
import { Router }                   from 'react-router';
import { createBrowserHistory }     from 'history';
import { ApolloProvider, useQuery } from '@apollo/react-hooks';
import { EnvironmentObject }        from './environments/types/base';
import { Modal }                    from 'semantic-ui-react';

// Semantic base css library, theme handled in webpack
import 'semantic-ui-less/semantic.less';

// Custom css library, supplemental to project
import './_sass/core.scss'

// Import store
import { createClient, hydrateClientState } from './store/_apollo';

// Import App root container
import App, { AppProps }        from './views/App';
import { SemanticUIColor }      from './types';
import { UserQueries }          from './queries';
import { BasicDialog }          from './views/components/modals/BasicDialog';
import { BasicDialogContainer } from './views/components/modals/BasicDialogContainer';
import { AuthZeroApolloClient } from './views/components/util/external/AuthZeroApolloClient';
import { AuthZeroProvider }     from './views/components/util/external/AuthZeroProvider';

// Setup render on the #root element once window is ready
( async ( window ) => {
	
	// Get the crafted environment var
	//@ts-ignore
	const environment: EnvironmentObject = __ENV__;
	
	// Setup Apollo client
	const apolloClient = createClient();
	
	// Hydrate Apollo client
	await hydrateClientState( apolloClient );
	
	//dev concerns
	if( process.env.NODE_ENV === 'development' ){
		
		const whyDidYouRender = require( '@welldone-software/why-did-you-render' );
		
		whyDidYouRender( React, {
			trackAllPureComponents: false,
			exclude:                [
				// /^Icon/,
				// /^AnimateInOut/,
				// /^Connect\(Droppable\)/,
			]
		} );
	}
	
	// Setup history, initialize store
	const history = createBrowserHistory( {
		
		      // base route path
		      basename: environment.route.baseRoute,
		
		      // on route prompts, create a custom modal instead of that ugl confirm dialog via browser
		      getUserConfirmation: async ( message: string, callback ) => {
			
			      try{
				
				      /* Test if we have a user the old fashion way: by tring to ping info about it within cache.
				      * If it's there, we know we havent wiped the cache and we can proceed with the warning */
				      let currentUser = await apolloClient.readQuery( {
					      query: UserQueries.queries.GET_CURRENT_USER()
				      } );
				
				      /* If we have a user... */
				      if( currentUser?.me ?? null ){
					
					      //create a dom element as a container
					      const modalContainer = document.createElement( 'div' );
					      document.body.appendChild( modalContainer )
					
					      // clean up method with response after container is removed
					      const respondAndDismissModal = ( response: boolean ) => {
						      ReactDOM.unmountComponentAtNode( modalContainer )
						      document.body.removeChild( modalContainer )
						      callback( response )
					      }
					
					      // Render a custom modal
					      ReactDOM.render( (
						      <Modal open={ true } size={ 'tiny' }>
							      <BasicDialog
								      hasSystemContext={ false }
								      onSubmit={ () => respondAndDismissModal( true ) }
								      onClose={ () => respondAndDismissModal( false ) }
								      className={ 'no-padding' }
								      submitLabel={ 'OK' }
								      cancelLabel={ 'Cancel' }
								      successToastLabel={ null }
								      errorToastLabel={ null }>
								
								      <BasicDialogContainer hasSystemContext={ false } title={ 'Confirm Navigation' } titleColor={ SemanticUIColor.ORANGE }>
									      { !_.isEmpty( message ) && <p>{ message }</p> }
									      <p><strong>Are you sure you want to proceed?</strong></p>
								      </BasicDialogContainer>
							
							      </BasicDialog>
						      </Modal>
					      ), modalContainer );
					
				      } else {
					
					      // No user data: this is a passthrough action, just confirm and move on
					      callback( true );
				      }
				
			      } catch( e ) {
				
				      // User ping failed, probably due to no auth available: this is a passthrough action, just confirm and move on
				      callback( true );
				
			      }
			
			      return Promise.resolve( true );
		      }
	      } ),
	      rootEl  = document.getElementById( 'root' );
	
	// Create render function
	const render = ( Component: FunctionComponent<AppProps>, el: Element ) => {
		
		ReactDOM.render(
			<Router history={ history }>
				<AuthZeroProvider>
					<AuthZeroApolloClient client={ apolloClient }>
						<Component/>
					
					</AuthZeroApolloClient>
				</AuthZeroProvider>
			</Router>, el );
	};
	
	render( App, rootEl );
	
} )( window );

export const Environment: FunctionComponent = ( props ) => {
	
	// Setup a reusable environment for storytelling
	//@ts-ignore
	const environment: EnvironmentObject = __ENV__,
	      apolloClient                   = createClient(),
	      history                        = createBrowserHistory( { basename: environment.route.baseRoute } );
	
	return (
		
		<ApolloProvider client={ apolloClient }>
			<Router history={ history }>
				<App>
					{ props.children }
				</App>
			</Router>
		</ApolloProvider>
	);
};
